import React from 'react';
import { PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline';

/**
 * Shared building blocks for TWDataTable column definitions.
 *
 * Per-view column arrays still live next to their views (e.g.
 * `views/orders/columns.js`) — this file holds the small atoms that every
 * table reuses: the header label class, common row-action buttons, and a
 * couple of formatting helpers that don't depend on view state.
 */

export const headerCls =
  'text-sm font-semibold text-slate-900 dark:text-slate-100';

export const colTitle = (label, { align } = {}) => (
  <span
    className={
      align === 'right' ? `${headerCls} block text-right` : headerCls
    }
  >
    {label}
  </span>
);

export const EditAction = ({ onClick, label = 'Edit', className = '' }) => (
  <button
    type="button"
    onClick={onClick}
    className={`inline-flex h-8 w-8 items-center justify-center rounded-full text-blue-500 transition hover:bg-blue-50 hover:text-blue-700 dark:text-sky-400 dark:hover:bg-sky-500/10 ${className}`}
    aria-label={label}
  >
    <PencilSquareIcon className="h-4 w-4" />
  </button>
);

export const DeleteAction = ({ onClick, label = 'Delete', className = '' }) => (
  <button
    type="button"
    onClick={onClick}
    className={`inline-flex h-8 w-8 items-center justify-center rounded-full text-rose-500 transition hover:bg-rose-50 hover:text-rose-600 dark:text-rose-400 dark:hover:bg-rose-500/10 ${className}`}
    aria-label={label}
  >
    <TrashIcon className="h-4 w-4" />
  </button>
);

/**
 * Two-line text cell: bold primary on top, muted secondary below.
 */
export const StackedText = ({ primary, secondary }) => (
  <div>
    <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
      {primary}
    </p>
    {secondary ? (
      <p className="text-xs font-medium text-slate-500 dark:text-slate-400">
        {secondary}
      </p>
    ) : null}
  </div>
);

/**
 * Generic light-weight skeleton row container. Pass `cells` (array of
 * widths in tailwind units like `w-32` or full classnames) to control the
 * shimmer placeholders. Used to preserve table dimensions during load.
 */
export const SkeletonCells = ({ cells = [] }) => (
  <>
    {cells.map((w, i) => (
      <td key={i} className="whitespace-nowrap py-4 px-4 first:pl-0 last:pr-0">
        <div className={`skeleton-bar h-4 ${w} rounded`} />
      </td>
    ))}
  </>
);

/**
 * Full-table skeleton placeholder matching the flat TWDataTable aesthetic.
 *
 * @param headers - array of column descriptors. Either a string ("Name") or
 *   `{ label, align, w, shape }` where:
 *     - label  : header text (string)
 *     - align  : 'left' (default) | 'right'
 *     - w      : tailwind width class for the body skeleton, e.g. 'w-32'
 *     - shape  : 'bar' (default) | 'pill' | 'circle' | 'avatar'
 * @param rows - number of skeleton rows (default 6)
 */
export const DataTableSkeleton = ({ headers = [], rows = 6 }) => {
  const columns = headers.map((h) =>
    typeof h === 'string' ? { label: h } : h,
  );
  return (
    <div className="reveal" aria-hidden="true">
      <table className="min-w-full divide-y divide-gray-300 dark:divide-slate-500">
        <thead>
          <tr>
            {columns.map((col, i) => (
              <th
                key={col.label || i}
                className={`py-3.5 ${i === 0 ? 'pr-3' : 'px-4'} text-sm font-semibold text-slate-900 dark:text-slate-100 ${
                  col.align === 'right' ? 'text-right' : 'text-left'
                }`}
              >
                {col.label || <span className="sr-only">Actions</span>}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 dark:divide-slate-500">
          {[...Array(rows)].map((_, idx) => (
            <tr key={idx}>
              {columns.map((col, i) => {
                const w = col.w || 'w-28';
                const right = col.align === 'right' ? 'ml-auto' : '';
                const cellClass = `whitespace-nowrap py-4 ${i === 0 ? 'pr-3' : 'px-4'} ${col.align === 'right' ? 'text-right' : ''}`;
                let placeholder;
                if (col.shape === 'circle') {
                  placeholder = (
                    <div
                      className={`skeleton-bar h-9 w-9 !rounded-full ${right}`}
                    />
                  );
                } else if (col.shape === 'avatar') {
                  placeholder = (
                    <div className="flex items-center gap-3">
                      <div className="skeleton-bar h-9 w-9 shrink-0 !rounded-full" />
                      <div className={`skeleton-bar h-4 ${w} rounded`} />
                    </div>
                  );
                } else if (col.shape === 'pill') {
                  placeholder = (
                    <div
                      className={`skeleton-bar h-6 ${w} !rounded-full ${right}`}
                    />
                  );
                } else {
                  placeholder = (
                    <div
                      className={`skeleton-bar h-4 ${w} rounded ${right}`}
                    />
                  );
                }
                return (
                  <td key={i} className={cellClass}>
                    {placeholder}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
